import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { currency, Stars, StatusBadge, BadgeTier } from "@/components/Bits";
import { MapPin, TrendingUp, Trophy, Clock, DollarSign, Crown, Sparkles, Plus } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/shifts", { params: { scope: user?.role === "comercio" ? "mine" : "assigned" } }),
        ]);
        setSummary(s.data);
        setShifts(l.data.shifts.slice(0, 5));
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (loading) return <div className="text-zinc-400">Carregando painel…</div>;

  const isComercio = user?.role === "comercio";

  return (
    <div className="space-y-8 anim-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
            Olá, {isComercio ? "Comércio" : "Profissional"}
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold">{user?.name}</h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            {summary?.badge && <BadgeTier badge={summary.badge} verified={user?.is_verified} />}
            {summary?.rating_count > 0 && (
              <span className="inline-flex items-center gap-2 text-sm text-zinc-300">
                <Stars value={summary.rating_avg} /> <span className="font-mono">{summary.rating_avg.toFixed(1)}</span>
                <span className="text-zinc-500">({summary.rating_count})</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {isComercio && <Link to="/shifts/new" className="cw-btn-primary inline-flex items-center gap-2" data-testid="btn-new-shift"><Plus className="w-4 h-4" /> Nova Diária</Link>}
          <Link to="/shifts" className="cw-btn-outline inline-flex items-center gap-2" data-testid="btn-browse-shifts"><MapPin className="w-4 h-4" /> Ver Diárias</Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isComercio ? (
          <>
            <StatCard icon={Clock} label="Diárias abertas" value={summary?.open_shifts || 0} color="text-emerald-400" />
            <StatCard icon={TrendingUp} label="Em andamento" value={summary?.active_shifts || 0} color="text-blue-400" />
            <StatCard icon={Trophy} label="Concluídas" value={summary?.completed_shifts || 0} color="text-amber-400" />
            <StatCard icon={DollarSign} label="Total gasto" value={currency(summary?.total_spent)} color="text-red-400" />
          </>
        ) : (
          <>
            <StatCard icon={Clock} label="Ativas" value={summary?.active_shifts || 0} color="text-emerald-400" />
            <StatCard icon={Trophy} label="Concluídas" value={summary?.completed_shifts || 0} color="text-amber-400" />
            <StatCard icon={DollarSign} label="Total ganho" value={currency(summary?.total_earned)} color="text-red-400" />
            <StatCard icon={Sparkles} label="Pontos" value={summary?.points || 0} color="text-purple-400" />
          </>
        )}
      </div>

      {/* Incentivo para açougueiro */}
      {!isComercio && summary && (
        <div className="cw-card p-6 bg-gradient-to-br from-zinc-900 to-red-950/30">
          <div className="flex items-center gap-3 mb-3">
            <Crown className="w-5 h-5 text-amber-400" />
            <h3 className="font-display font-bold text-lg">Programa Fidelidade</h3>
          </div>
          <p className="text-sm text-zinc-300 mb-3">
            Sequência atual: <b className="text-white font-mono">{summary.consecutive_completed} diárias</b>.
            Complete <b className="text-amber-400">{Math.max(0, 5 - summary.consecutive_completed)}</b> a mais para desbloquear o Selo Ouro e ganhar bônus de +5%.
          </p>
          <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-red-500" style={{width: `${Math.min(100, (summary.consecutive_completed/5)*100)}%`}} />
          </div>
        </div>
      )}

      {/* Recent shifts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-display font-bold">Suas diárias recentes</h2>
          <Link to="/shifts" className="text-sm text-red-500 hover:underline">Ver todas →</Link>
        </div>
        {shifts.length === 0 ? (
          <div className="cw-card p-8 text-center text-zinc-400">
            {isComercio ? "Você ainda não publicou diárias." : "Você ainda não aceitou diárias."}
          </div>
        ) : (
          <div className="grid gap-3">
            {shifts.map(s => (
              <Link key={s.shift_id} to={`/shifts/${s.shift_id}`} className="cw-card p-4 flex items-center justify-between hover:bg-zinc-900/60" data-testid={`shift-card-${s.shift_id}`}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{s.title}</h3>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xs text-zinc-400">{s.date} · {s.start_time}–{s.end_time} · {s.city}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-display font-bold text-red-500">{currency(s.final_rate)}</div>
                  <div className="text-xs text-zinc-500">Diária</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="cw-card p-5">
    <Icon className={`w-5 h-5 mb-3 ${color}`} />
    <div className="text-2xl font-display font-bold">{value}</div>
    <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{label}</div>
  </div>
);
